import React from 'react'
import { getDotIndexes, getNextMoveIndex, getPercentOffsetForSlide, getPrevMoveIndex, useTween } from './carousel-helpers'
import { useCarouselContext } from './carousel-context'

export function useCarouselDragging({
  onDragStart,
  onDrag,
  onDragEnd,
  onUserNavigation,
  dragThreshold: propsDragThreshold,
  mobileDraggingEnabled,
  desktopDraggingEnabled,
  easing,
  edgeEasing,
  disableEdgeSwiping
}: {
  onDragStart: (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => void
  onDrag: (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => void
  onDragEnd: (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => void
  onUserNavigation: (e: React.TouchEvent | React.MouseEvent | React.KeyboardEvent) => void
  dragThreshold: number
  mobileDraggingEnabled: boolean
  desktopDraggingEnabled: boolean
  easing: (normalizedTime: number) => number
  edgeEasing: (normalizedTime: number) => number
  disableEdgeSwiping: boolean
}) {
  const {
    wrapAround,
    slideCount,
    slidesToShow,
    cellAlign,
    slidesToScroll,
    currentSlide,
    animationDistance,
    disableAnimation,
    speed,
    goToSlide,
    slideContainerRef,
    slideViewpointRef
  } = useCarouselContext()

  const [isDragging, setIsDragging] = React.useState<boolean>(false)
  const [dragDistance, setDragDistance] = React.useState<number>(0)

  const preDragOffset = React.useRef<number>(0)
  const draggedOffset = preDragOffset.current - dragDistance

  const percentOffsetForSlideProps = [slideCount, slidesToShow, cellAlign, wrapAround] as const

  // We recycle dot index generation to determine the leftmost and rightmost
  // indices used, to be used in calculating the x-translation values we need
  // to limit to or when edgeEasing should be used.
  const dotIndexes = getDotIndexes(slideCount, slidesToScroll, slidesToShow, wrapAround, cellAlign)

  let clampedDraggedOffset = `${draggedOffset}px`
  if (isDragging && disableEdgeSwiping && !wrapAround) {
    const clampOffsets = [dotIndexes[0], dotIndexes[dotIndexes.length - 1]].map((index) =>
      getPercentOffsetForSlide(index, ...percentOffsetForSlideProps)
    )
    // Offsets are seemingly backwards because the rightmost slide creates
    // the most negative translate value
    clampedDraggedOffset = `clamp(${clampOffsets[1]}%, ${draggedOffset}px, ${clampOffsets[0]}%)`
  }

  const slideBasedOffset = getPercentOffsetForSlide(currentSlide, ...percentOffsetForSlideProps)

  const isEdgeEasing =
    !disableEdgeSwiping &&
    !wrapAround &&
    ((currentSlide === dotIndexes[0] && animationDistance < 0) ||
      (currentSlide === dotIndexes[dotIndexes.length - 1] && animationDistance > 0))
  const { value: transition, isAnimating } = useTween(
    speed,
    !isEdgeEasing ? easing : edgeEasing,
    // animationDistance is assumed to be unique enough that it can be used to
    // detect when a new animation should start. This is used in addition to
    // currentSlide because some animations, such as those with edgeEasing, do
    // not occur due to a change in value of currentSlide
    currentSlide + animationDistance,
    isDragging || disableAnimation
  )

  // Return undefined if the transform would be 0 pixels since transforms can
  // cause flickering in chrome.
  let positioning: string | undefined
  if (isDragging || slideBasedOffset !== 0 || isAnimating) {
    if (isDragging) {
      positioning = `translateX(${clampedDraggedOffset})`
    } else {
      const transitionOffset = isAnimating ? (1 - transition) * animationDistance : 0
      positioning = `translateX(calc(${slideBasedOffset}% - ${transitionOffset}px))`
    }
  }

  const prevDragged = React.useRef(false)
  const prevXPosition = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (isDragging) prevDragged.current = true

    if (!(isDragging || isAnimating)) {
      // Wait for the animation to complete after dragging
      prevDragged.current = false
    }
  }, [isAnimating, isDragging])

  const dragPositions = React.useRef<{ pos: number; time: number }[]>([])

  const handleDragEnd = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !slideContainerRef.current) return

    setIsDragging(false)

    // Inertia calculation is used to allow quick flicks to scroll the carousel
    // where they might not based on the start and end points of the gesture
    // alone. In certain conditions, the inertia may also scroll the carousel
    // several times.
    let distanceFromInertia = 0
    if (dragPositions.current.length > 1) {
      const startMove = dragPositions.current[0]
      const endMove = dragPositions.current[dragPositions.current.length - 1]
      const timeOffset = endMove.time - startMove.time
      const goodInertiaFeelConstant = 9
      const goodFrictionFeelConstant = 0.92
      const initialVelocity = goodInertiaFeelConstant * Math.abs((endMove.pos - startMove.pos) / timeOffset)
      let velocity = initialVelocity

      while (Math.abs(velocity) > 1) {
        distanceFromInertia += velocity
        velocity *= goodFrictionFeelConstant
      }
    }
    dragPositions.current = []

    const adjustedDragDistance = Math.abs(dragDistance) + Math.abs(distanceFromInertia)

    onDragEnd(e)

    prevXPosition.current = null
    setDragDistance(0)

    const oneScrollWidth = slideContainerRef.current.offsetWidth * Math.min(1, slidesToScroll / slidesToShow)
    const dragThreshold = oneScrollWidth * propsDragThreshold

    if (adjustedDragDistance < dragThreshold) {
      goToSlide(currentSlide)
      return
    }

    // If skipping over multiple slides at a time is still roughly trackable by
    // your eyes, we allow for skipping multiple slides with a single gesture.
    // This formula is just based off an observation that it is confusing to
    // skip from slides 1 to 3 when only one slide is shown at a time, but
    // skipping from 1 to 4 or so with two slides shown at a time is pulled-back
    // enough that you can still roughly keep track of your place in the
    // carousel.
    const canMaintainVisualContinuity = slidesToShow >= 2 * slidesToScroll
    const timesToMove = canMaintainVisualContinuity
      ? 1 + Math.floor((adjustedDragDistance - dragThreshold) / oneScrollWidth)
      : 1

    let nextSlideIndex = currentSlide
    for (let index = 0; index < timesToMove; index += 1) {
      if (dragDistance > 0) {
        nextSlideIndex = getNextMoveIndex(wrapAround, nextSlideIndex, slideCount, slidesToScroll, slidesToShow, cellAlign)
      } else {
        nextSlideIndex = getPrevMoveIndex(wrapAround, nextSlideIndex, slidesToScroll, slidesToShow, cellAlign)
      }
    }

    if (nextSlideIndex !== currentSlide) {
      onUserNavigation(e)
    }

    goToSlide(nextSlideIndex)
  }

  const onTouchStart = React.useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!mobileDraggingEnabled || !slideViewpointRef.current || !slideContainerRef.current) {
        return
      }
      setIsDragging(true)
      preDragOffset.current =
        slideViewpointRef.current.getBoundingClientRect().left - slideContainerRef.current.getBoundingClientRect().left

      onDragStart(e)
    },
    [slideContainerRef, onDragStart, mobileDraggingEnabled, slideViewpointRef]
  )

  const handlePointerMove = React.useCallback(
    (xPosition: number) => {
      if (!isDragging) return

      const isFirstMove = prevXPosition.current === null
      const delta = prevXPosition.current !== null ? xPosition - prevXPosition.current : 0
      const nextDragDistance = dragDistance + delta

      const now = Date.now()
      // Maintain a buffer of drag positions from the last 100ms
      while (dragPositions.current.length > 0) {
        if (now - dragPositions.current[0].time <= 100) {
          break
        }
        dragPositions.current.shift()
      }
      dragPositions.current.push({ pos: nextDragDistance, time: now })

      if (!isFirstMove) {
        // nextDragDistance will always be `0` on the first move event, so we
        // skip it because the value is already set to 0 at this point
        setDragDistance(nextDragDistance)
      }

      prevXPosition.current = xPosition
    },
    [isDragging, dragDistance]
  )

  const onTouchMove = React.useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isDragging || !slideContainerRef.current) return

      onDragStart(e)

      const moveValue = slideContainerRef.current.offsetWidth - e.touches[0].pageX

      handlePointerMove(moveValue)
    },
    [isDragging, slideContainerRef, handlePointerMove, onDragStart]
  )

  const onMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!desktopDraggingEnabled || !slideViewpointRef.current || !slideContainerRef.current) return

      setIsDragging(true)

      preDragOffset.current =
        slideViewpointRef.current.getBoundingClientRect().left - slideContainerRef.current.getBoundingClientRect().left

      onDragStart(e)
    },
    [slideContainerRef, desktopDraggingEnabled, onDragStart, slideViewpointRef]
  )

  const onMouseMove = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !slideContainerRef.current) return

      onDrag(e)

      const offsetX = e.clientX - slideContainerRef.current.getBoundingClientRect().left
      const moveValue = slideContainerRef.current.offsetWidth - offsetX

      handlePointerMove(moveValue)
    },
    [slideContainerRef, isDragging, handlePointerMove, onDrag]
  )

  const onMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    handleDragEnd(e)
  }

  return {
    onMouseUp,
    onMouseLeave: onMouseUp,
    onMouseDown,
    onMouseMove,
    onTouchStart,
    handleDragEnd,
    onTouchMove,
    onTouchEnd: handleDragEnd,
    positioning
  }
}
